import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../hooks/useTypedSelector";
import {
  getCarWithContext,
  updateRequest,
  createRequest,
  fetchUserRelations,
  fetchPickupDeliveryDates,
  clearCarWithContext,
  fetchCarQuickAddOptions,
} from "../../infrastructure/slices/requestQuickAddSlice";
import {
  updateRequestState,
  getRequest,
} from "../../infrastructure/slices/requestMutateSlice";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { toast } from "sonner";

// Component imports
import { DealerSelectSection } from "./components/DealerSelectSection";
import { LicensePlateSelection } from "./components/LicensePlateSelection";
import { RequestTypeSelection } from "./components/RequestTypeSelection";
import { TiresetSelection } from "./components/TiresetSelection";
import { ActiveRequestManagement } from "./components/ActiveRequestManagement";
import { NewRequestForm } from "./components/NewRequestForm";
import { DatePickerProps } from "react-datepicker";
import { useAuthorization } from "../../../../hooks/useAuthorization";

type RequestType = "deliver" | "pickup";

export const RequestQuickAddForm = () => {
  const { t } = useTranslation("common");
  const [selectedLicense, setSelectedLicense] = useState<string | null>(null);
  const [isCustomLicense, setIsCustomLicense] = useState<boolean>(false);
  const [requestType, setRequestType] = useState<RequestType | null>(null);
  const [selectedRelation, setSelectedRelation] = useState<string | null>(null);
  const [selectedTireset, setSelectedTireset] = useState<string | null>(null);
  const [showTiresetSelect, setShowTiresetSelect] = useState<boolean>(false);
  const [workOrderNumber, setWorkOrderNumber] = useState<string>("");
  const [pickupDeliveryDate, setPickupDeliveryDate] = useState<string>("");
  const [tireSetType, setTireSetType] = useState<string>("");
  const [isEditingActiveRequest, setIsEditingActiveRequest] = useState(false);
  const [manualRequestTypeChange, setManualRequestTypeChange] = useState(false);
  const [manualTiresetSelection, setManualTiresetSelection] = useState(false);
  const manualTiresetSelectionRef = useRef(false);

  const dispatch = useAppDispatch();
  const { car, loading, relations, pickupDeliveryDates } = useAppSelector(
    (state) => state.requestQuickAdd,
  );
  const { hasRole } = useAuthorization();

  // Only fetch car data when we have an existing license and want to show active request info
  const [shouldShowExistingCarData, setShouldShowExistingCarData] =
    useState<boolean>(false);

  useEffect(() => {
    if (
      selectedLicense &&
      !isCustomLicense &&
      shouldShowExistingCarData &&
      (!car || `${car?.car?.id}` !== selectedLicense)
    ) {
      void dispatch(getCarWithContext(selectedLicense));
    }
  }, [selectedLicense, car, isCustomLicense, shouldShowExistingCarData]);

  // For custom licenses, we won't have car data until we save
  const selectedCar = useMemo(
    () =>
      !loading &&
      car?.car &&
      car?.car?.id?.toString() === selectedLicense &&
      !isCustomLicense &&
      shouldShowExistingCarData
        ? car
        : null,
    [loading, car, selectedLicense, isCustomLicense, shouldShowExistingCarData],
  );

  // Handle license plate selection from the component
  const handleLicensePlateSelection = useCallback(
    (license: string | null, isCustom: boolean) => {
      setSelectedLicense(license);
      setIsCustomLicense(isCustom);

      // Reset other form fields
      setRequestType(isCustom ? "pickup" : null); // Force pickup for custom licenses
      setSelectedTireset(null);
      setShowTiresetSelect(false);
      setTireSetType("");
      setShouldShowExistingCarData(!isCustom); // Only fetch car data for existing cars
      setPickupDeliveryDate(""); // Reset pickup/delivery date
      setWorkOrderNumber(""); // Reset work order number
      setManualTiresetSelection(false); // Reset manual selection flag
      manualTiresetSelectionRef.current = false; // Reset ref
    },
    [],
  );

  // State for storing prediction reason
  const [predictionReason, setPredictionReason] = useState<string | null>(null);

  // Predict request type based on car data
  useEffect(() => {
    if (selectedCar && requestType === null && !isEditingActiveRequest) {
      const backendDefault = selectedCar.defaultRequestType;
      if (backendDefault) {
        setRequestType(backendDefault.type);
        setPredictionReason(backendDefault.reason);
        setManualRequestTypeChange(false);
      } else {
        // Fallback to frontend logic if backend value is missing
        setRequestType("pickup");
        setManualRequestTypeChange(false);
      }
    }
  }, [selectedCar, requestType, isEditingActiveRequest]);

  // Select appropriate tireset based on car data and request type
  useEffect(() => {
    if (selectedCar && requestType) {
      if (isEditingActiveRequest) return;
      if (manualTiresetSelectionRef.current) return;
      // Use backend-provided tireSetSelection if available
      const selection = selectedCar.tireSetSelection?.[requestType];
      if (selection && selection.tireset) {
        setSelectedTireset(selection.tireset);
        setShowTiresetSelect(false);
        setPredictionReason(selection.reason || null); // Always use API reason
      } else {
        setSelectedTireset(null);
        setShowTiresetSelect(true);
        setPredictionReason(null);
      }
    }
  }, [
    selectedCar,
    requestType,
    isEditingActiveRequest,
    manualRequestTypeChange,
  ]);

  // Reset selectedTireset if it becomes invalid for the new requestType
  useEffect(() => {
    if (selectedCar && requestType && selectedTireset) {
      // Don't validate if user manually selected - let them keep their choice
      if (manualTiresetSelection) {
        return;
      }

      const detail = selectedCar.tireset_details.find(
        (d) => d.tireset.id === selectedTireset,
      );
      if (detail) {
        const isValid =
          (requestType === "deliver" && detail.tireset.in_hotel === true) ||
          (requestType === "pickup" && detail.tireset.in_hotel === false);
        if (!isValid) {
          setSelectedTireset(null);
          setShowTiresetSelect(true);
        }
      }
    }
  }, [selectedCar, requestType, selectedTireset, manualTiresetSelection]);

  const selectedDetail = useMemo(
    () =>
      selectedCar
        ? selectedCar.tireset_details.find(
            (detail) => detail.tireset.id === selectedTireset,
          )
        : null,
    [selectedCar, selectedTireset],
  );

  // Extract active request information for easier access
  // When request type changes but tireset hasn't been selected yet, check all tiresets for the given request type
  const activeRequest = useMemo(() => {
    if (selectedDetail) {
      const pickupReq = selectedDetail.request_details.active_pickup_request;
      const deliveryReq =
        selectedDetail.request_details.active_delivery_request;

      return {
        pickup: pickupReq,
        delivery: deliveryReq,
        hasActiveRequest: !!(pickupReq || deliveryReq),
        activeRequest: pickupReq || deliveryReq,
        activeRequestId: pickupReq?.id || deliveryReq?.id,
      };
    }

    // If no selected detail but we have a car and request type, check for any active requests of that type
    if (selectedCar && requestType && !selectedTireset) {
      const tiresetsForType = selectedCar.tireset_details.filter((detail) => {
        if (requestType === "deliver") {
          return detail.tireset.in_hotel === true;
        } else if (requestType === "pickup") {
          return detail.tireset.in_hotel === false;
        }
        return true;
      });

      // Check if any of these tiresets have an active request
      for (const detail of tiresetsForType) {
        const pickupReq = detail.request_details.active_pickup_request;
        const deliveryReq = detail.request_details.active_delivery_request;

        // Check for the active request matching the current request type
        const activeReq = requestType === "pickup" ? pickupReq : deliveryReq;

        if (activeReq) {
          return {
            pickup: pickupReq,
            delivery: deliveryReq,
            hasActiveRequest: true,
            activeRequest: activeReq,
            activeRequestId: activeReq.id,
          };
        }
      }
    }

    return null;
  }, [selectedDetail, selectedCar, requestType, selectedTireset]);

  // Check if there are valid tiresets for the selected request type
  const validTiresetsForRequestType = useMemo(
    () =>
      selectedCar && requestType
        ? selectedCar.tireset_details.filter((detail) => {
            if (requestType === "deliver") {
              return detail.tireset.in_hotel === true;
            } else if (requestType === "pickup") {
              return detail.tireset.in_hotel === false;
            }
            return true;
          })
        : [],
    [selectedCar, requestType],
  );

  const hasNoValidTiresets = useMemo(
    () =>
      !!(
        selectedCar &&
        requestType &&
        validTiresetsForRequestType.length === 0
      ),
    [selectedCar, requestType, validTiresetsForRequestType],
  );

  const [editingValues, setEditingValues] = useState<{
    workOrderNumber: string;
    pickupDeliveryDate: string;
  }>({
    workOrderNumber: "",
    pickupDeliveryDate: "",
  });

  useEffect(() => {
    if (activeRequest?.activeRequest) {
      setEditingValues({
        workOrderNumber: activeRequest.activeRequest.work_order_number || "",
        pickupDeliveryDate:
          activeRequest.activeRequest.pickup_and_delivery_date || "",
      });
    }
  }, [activeRequest?.activeRequest]);

  const handleEditRequestSubmit = useCallback(() => {
    if (!activeRequest?.activeRequest || !activeRequest.activeRequestId) {
      toast.error(t("validation.no_active_request"));
      return;
    }

    // Validate required fields
    if (!editingValues.pickupDeliveryDate) {
      toast.error(t("validation.pickup_delivery_date_required"));
      return;
    }

    // Validate that the date is not in the past
    const selectedDate = new Date(editingValues.pickupDeliveryDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      toast.error(t("validation.pickup_delivery_date_past"));
      return;
    }

    // Validate work order number length
    if (
      editingValues.workOrderNumber &&
      editingValues.workOrderNumber.length > 255
    ) {
      toast.error(t("validation.work_order_number_length"));
      return;
    }

    // Show loading state
    void Swal.fire({
      title: t("request.updating"),
      text: t("request.updating_description"),
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    // Use the updateRequest thunk from requestQuickAddSlice
    void dispatch(
      updateRequest({
        id: activeRequest.activeRequestId,
        work_order_number: editingValues.workOrderNumber || null,
        pickup_and_delivery_date: editingValues.pickupDeliveryDate,
        type: activeRequest.pickup ? "pickup" : "deliver",
      }),
    )
      .unwrap()
      .then(() => {
        // Close loading modal
        Swal.close();

        // Refresh the request data
        if (activeRequest.activeRequestId) {
          void dispatch(getRequest(activeRequest.activeRequestId.toString()));
        }

        // Refresh the car data to get updated request info
        // BUT preserve current selections by not clearing form state
        if (selectedLicense && !isCustomLicense) {
          void dispatch(getCarWithContext(selectedLicense));
          // Note: We don't reset form state here to preserve selections
        }

        // Exit editing mode
        setIsEditingActiveRequest(false);

        // Show success message
        toast.success(t("request.updated_successfully"));
      })
      .catch((error: unknown) => {
        // Close loading modal
        Swal.close();

        console.error("Failed to update request:", error);

        // Error is already handled by the slice and shown via toast
      });
  }, [
    activeRequest,
    editingValues,
    dispatch,
    t,
    selectedLicense,
    isCustomLicense,
  ]);

  const handleNewRequestSubmit = useCallback(() => {
    // Validate required fields
    if (!requestType) {
      toast.error(t("validation.request_type_required"));
      return;
    }

    if (!pickupDeliveryDate) {
      toast.error(t("validation.pickup_delivery_date_required"));
      return;
    }

    // Validate that the date is not in the past
    const selectedDate = new Date(pickupDeliveryDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      toast.error(t("validation.pickup_delivery_date_past"));
      return;
    }

    if (!selectedRelation) {
      toast.error(t("validation.dealer_required"));
      return;
    }

    // Additional validation for custom license
    if (isCustomLicense) {
      if (!selectedLicense) {
        toast.error(t("validation.license_plate_required"));
        return;
      }

      if (!tireSetType) {
        toast.error(t("validation.tire_set_type_required"));
        return;
      }

      if (requestType !== "pickup") {
        toast.error(t("validation.new_cars_pickup_only"));
        return;
      }
    } else {
      // Validation for existing cars
      if (!selectedTireset) {
        toast.error(t("validation.tire_set_required"));
        return;
      }
    }

    // Validate work order number length
    if (workOrderNumber && workOrderNumber.length > 255) {
      toast.error(t("validation.work_order_number_length"));
      return;
    }

    // Show loading state
    void Swal.fire({
      title: t("request.creating"),
      text: t("request.creating_description"),
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    // Prepare request data for both scenarios
    const baseRequestData = {
      type: requestType,
      work_order_number: workOrderNumber || null,
      pickup_and_delivery_date: pickupDeliveryDate,
      relation_id: selectedRelation,
    };

    if (isCustomLicense && selectedLicense && tireSetType) {
      // Scenario 1: Custom license plate (create car + tireset + request)
      const newCarRequestData = {
        ...baseRequestData,
        licence: selectedLicense,
        tireset_type: tireSetType,
      };

      void dispatch(createRequest(newCarRequestData))
        .unwrap()
        .then(() => {
          // Close loading modal
          Swal.close();

          toast.success(t("request.created_successfully"));

          // Reset form
          setSelectedLicense(null);
          setIsCustomLicense(false);
          setRequestType(null);
          setSelectedRelation(null);
          setSelectedTireset(null);
          setShowTiresetSelect(false);
          setWorkOrderNumber("");
          setPickupDeliveryDate("");
          setTireSetType("");
          dispatch(clearCarWithContext());
          void dispatch(fetchCarQuickAddOptions(""));
        })
        .catch((error: unknown) => {
          // Close loading modal
          Swal.close();

          // Error is already handled by the slice and shown via toast
          console.error("Failed to create request:", error);
        });
    } else if (selectedTireset) {
      // Scenario 2: Existing car (create request for existing tireset)
      const existingCarRequestData = {
        ...baseRequestData,
        tireset_id: selectedTireset,
        car_id: selectedLicense || undefined,
      };

      void dispatch(createRequest(existingCarRequestData))
        .unwrap()
        .then(() => {
          // Close loading modal
          Swal.close();

          // Show success message
          toast.success(t("request.created_successfully"));

          // Reset form
          setSelectedLicense(null);
          setIsCustomLicense(false);
          setRequestType(null);
          setSelectedRelation(null);
          setSelectedTireset(null);
          setShowTiresetSelect(false);
          setWorkOrderNumber("");
          setPickupDeliveryDate("");
          setTireSetType("");
          dispatch(clearCarWithContext());
          void dispatch(fetchCarQuickAddOptions(""));
        })
        .catch((error: unknown) => {
          // Close loading modal
          Swal.close();

          // Error is already handled by the slice and shown via toast
          console.error("Failed to create request:", error);
        });
    } else {
      // Close loading modal first
      Swal.close();

      toast.error(t("validation.tire_set_or_license_required"));
    }
  }, [
    requestType,
    pickupDeliveryDate,
    selectedRelation,
    workOrderNumber,
    isCustomLicense,
    selectedLicense,
    tireSetType,
    selectedTireset,
    dispatch,
    t,
  ]);

  const handleCancelActiveRequest = useCallback(() => {
    if (!activeRequest?.activeRequest || !activeRequest.activeRequestId) {
      return;
    }

    void Swal.fire({
      title: t("request.cancel_confirmation_title"),
      text: t("request.cancel_confirmation_text"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: t("request.cancel_confirmation_yes"),
      cancelButtonText: t("request.cancel_confirmation_no"),
    }).then((result) => {
      if (result.isConfirmed && activeRequest.activeRequestId) {
        // Show loading state
        void Swal.fire({
          title: t("request.cancelling"),
          text: t("request.cancelling_description"),
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        // Use the updateRequestState action to cancel the request
        void dispatch(
          updateRequestState({
            id: activeRequest.activeRequestId,
            state: "cancelled",
          }),
        )
          .unwrap()
          .then(() => {
            // Close loading modal
            Swal.close();

            // Refresh the car data to update the UI
            if (selectedLicense && !isCustomLicense) {
              void dispatch(getCarWithContext(selectedLicense));
            }

            toast.success(t("request.cancelled_successfully"));
          })
          .catch((error: unknown) => {
            // Close loading modal
            Swal.close();

            toast.error(t("request.cancel_failed"));
            console.error("Failed to cancel request:", error);
          });
      }
    });
  }, [activeRequest, dispatch, t, selectedLicense, isCustomLicense]);

  useEffect(() => {
    void dispatch(fetchUserRelations());
    void dispatch(fetchPickupDeliveryDates());
  }, [dispatch]);

  // Auto-select relation if only one is available
  useEffect(() => {
    if (relations.length === 1 && selectedRelation !== relations[0].id) {
      setSelectedRelation(relations[0].id);
    }
  }, [relations, selectedRelation]);

  const handleRelationChange = useCallback((id: string) => {
    setSelectedRelation(id);
  }, []);

  const handleTiresetChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      manualTiresetSelectionRef.current = true; // Set ref immediately
      setSelectedTireset(e.target.value || null);
      setManualTiresetSelection(true); // Mark as manually selected
    },
    [],
  );

  const handleDeliveryTypeChange = useCallback(
    (type: RequestType) => {
      if (type === "deliver" && isCustomLicense) return; // Don't allow delivery for custom licenses
      setRequestType(type);
      setPredictionReason(null); // Clear prediction reason when user manually changes
      setManualRequestTypeChange(true); // Mark as manual change
      // Reset tireset selection to force re-selection based on new request type
      setSelectedTireset(null);
      setShowTiresetSelect(false);
      setManualTiresetSelection(false); // Reset manual selection flag
      manualTiresetSelectionRef.current = false; // Reset ref

      // Refresh car data to get latest active request information
      if (selectedLicense && !isCustomLicense) {
        void dispatch(getCarWithContext(selectedLicense));
      }
    },
    [isCustomLicense, selectedLicense, dispatch],
  );

  const handleEditingToggle = useCallback(() => {
    setIsEditingActiveRequest((prev) => !prev);
  }, []);

  // Build datePickerOptions as a plain object for react-datepicker
  const datePickerOptions: Partial<DatePickerProps> = {
    filterDate: (date: Date) => {
      // Disable weekends
      const day = date.getDay();
      return day !== 0 && day !== 6;
    },
    monthsShown: 2,
    // Add more options as needed, e.g. minDate, highlightDates, excludeDates
  };

  // The administrators can change the date before the delivery date.
  if (!hasRole("super-admin") && !hasRole("dbh-manager") && selectedDetail) {
    if (selectedDetail?.tireset?.getPickupDeliveryDates?.firstDate) {
      datePickerOptions.minDate = new Date(
        selectedDetail.tireset.getPickupDeliveryDates.firstDate,
      );
    }
  } else if (selectedDetail?.tireset?.getPickupDeliveryDates?.firstDate) {
    datePickerOptions.highlightDates = [
      {
        "fw-bold": [
          new Date(selectedDetail?.tireset?.getPickupDeliveryDates?.firstDate),
        ],
      },
    ];
  } else if (pickupDeliveryDates?.firstDate) {
    // Fallback: use pickupDeliveryDates from slice if no selectedDetail
    datePickerOptions.minDate = new Date(pickupDeliveryDates.firstDate);
    datePickerOptions.highlightDates = [
      { "fw-bold": [new Date(pickupDeliveryDates.firstDate)] },
    ];
  }

  if (selectedDetail?.tireset?.getPickupDeliveryDates?.forbiddenDates) {
    datePickerOptions.excludeDates =
      selectedDetail?.tireset?.getPickupDeliveryDates?.forbiddenDates.map(
        (item) => {
          return new Date(item);
        },
      );
  } else if (pickupDeliveryDates?.forbiddenDates) {
    // Fallback: use pickupDeliveryDates forbiddenDates if no selectedDetail
    datePickerOptions.excludeDates = pickupDeliveryDates.forbiddenDates.map(
      (item) => new Date(item),
    );
  }

  // Set default value for pickupDeliveryDate to minDate when creating a new request
  useEffect(() => {
    const shouldSetDate =
      (!pickupDeliveryDate && requestType && datePickerOptions?.minDate) ||
      (datePickerOptions?.minDate &&
        pickupDeliveryDate &&
        datePickerOptions.minDate > new Date(pickupDeliveryDate));

    if (shouldSetDate) {
      const minDateObj = datePickerOptions.minDate;
      if (minDateObj instanceof Date && !isNaN(minDateObj.getTime())) {
        const year = minDateObj.getFullYear();
        const month = String(minDateObj.getMonth() + 1).padStart(2, "0");
        const day = String(minDateObj.getDate()).padStart(2, "0");
        setPickupDeliveryDate(`${year}-${month}-${day}`);
      }
    }
  }, [pickupDeliveryDate, requestType, datePickerOptions?.minDate]);

  // Reset predictionReason when switching cars or license type
  useEffect(() => {
    setPredictionReason(null);
  }, [selectedLicense, isCustomLicense]);

  // Only show predictionReason if requestType matches backend default
  const showPredictionReason = useMemo(() => {
    if (!selectedCar || !selectedCar.defaultRequestType) return false;
    return (
      requestType === selectedCar.defaultRequestType.type && !!predictionReason
    );
  }, [selectedCar, requestType, predictionReason]);

  const isPickupDisabled = () => {
    if (selectedDetail) {
      return selectedDetail.is_pickup_disabled;
    }

    return false;
  };

  return (
    <>
      <form>
        <DealerSelectSection
          relations={relations}
          selectedRelation={selectedRelation}
          onRelationChange={handleRelationChange}
        />

        <LicensePlateSelection
          selectedLicense={selectedLicense}
          isCustomLicense={isCustomLicense}
          onLicensePlateChange={handleLicensePlateSelection}
        />

        {selectedLicense && (
          <RequestTypeSelection
            requestType={requestType}
            isCustomLicense={isCustomLicense}
            onRequestTypeChange={handleDeliveryTypeChange}
            predictedReason={showPredictionReason ? predictionReason : null}
          />
        )}

        <TiresetSelection
          isPickupDisabled={isPickupDisabled}
          selectedCar={selectedCar}
          selectedTireset={selectedTireset}
          showTiresetSelect={showTiresetSelect}
          requestType={requestType}
          hasNoValidTiresets={hasNoValidTiresets}
          onTiresetChange={handleTiresetChange}
        />

        {!(requestType === "deliver" && isPickupDisabled()) && (
          <ActiveRequestManagement
            requestType={requestType}
            activeRequest={activeRequest}
            isEditingActiveRequest={isEditingActiveRequest}
            editingValues={editingValues}
            onEditingToggle={handleEditingToggle}
            onCancelActiveRequest={handleCancelActiveRequest}
            onEditRequestSubmit={handleEditRequestSubmit}
            onEditingValuesChange={setEditingValues}
            datePickerOptions={datePickerOptions}
          />
        )}

        {/* Show form when no warnings are displayed, and we have either a selected tireset or no car (but with request type) */}
        {(() => {
          const showForm =
            (!hasNoValidTiresets || selectedTireset) &&
            !activeRequest?.hasActiveRequest &&
            requestType &&
            (requestType !== "pickup" || !isPickupDisabled()) &&
            (selectedTireset || !selectedCar || showTiresetSelect);

          return showForm ? (
            <NewRequestForm
              requestType={requestType}
              workOrderNumber={workOrderNumber}
              pickupDeliveryDate={pickupDeliveryDate}
              tireSetType={tireSetType}
              selectedCar={selectedCar}
              onWorkOrderNumberChange={setWorkOrderNumber}
              onPickupDeliveryDateChange={setPickupDeliveryDate}
              onTireSetTypeChange={setTireSetType}
              onNewRequestSubmit={handleNewRequestSubmit}
              datePickerOptions={datePickerOptions}
            />
          ) : null;
        })()}
      </form>
    </>
  );
};
